import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  AdminCreateTeamMemberRequest,
  AdminSessionResponse,
  AdminTeamCatalog,
} from "@ternary/contracts"
import {
  Badge,
  Button,
  breakable,
  type Column,
  Dialog,
  Field,
  Input,
  SearchInput,
  Select,
  Table,
} from "@ternary/ui"
import { type FormEvent, useId, useState } from "react"
import {
  createAdminTeamMember,
  deleteAdminTeamMembers,
  inviteAdminTeamMembers,
  loadAdminTeam,
} from "../api.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"
import { SettingsHeader } from "./admin-settings-page.js"

type TeamMember = AdminTeamCatalog["members"][number]

/** The invite form. Its errors, the API's included, show inside the dialog. */
function TeamInviteForm({
  error,
  onClose,
  onSave,
  saving,
}: {
  error: string
  onClose(): void
  onSave(member: AdminCreateTeamMemberRequest): void
  saving: boolean
}) {
  const formId = useId()
  const [email, setEmail] = useState("")
  const [access, setAccess] = useState<"read" | "write">("read")

  return (
    <Dialog
      description="They get an email with a link to set their password."
      footer={
        <>
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button form={formId} loading={saving} type="submit">
            Invite teammate
          </Button>
        </>
      }
      onClose={onClose}
      open
      title="Invite teammate"
    >
      <form
        className="admin-stack"
        id={formId}
        onSubmit={(event: FormEvent) => {
          event.preventDefault()
          onSave({ access, email })
        }}
      >
        <Field label="Email" required>
          <Input
            autoComplete="off"
            onChange={(event) => setEmail(event.target.value)}
            placeholder="teammate@northwind.example"
            type="email"
            value={email}
          />
        </Field>
        <Field
          hint="Read can view the dashboard but not create, edit or delete anything."
          label="Access"
        >
          <Select
            onChange={(event) => setAccess(event.target.value as "read" | "write")}
            value={access}
          >
            <option value="read">Read</option>
            <option value="write">Write</option>
          </Select>
        </Field>
        {error ? (
          <p className="tny-error" role="alert">
            {error}
          </p>
        ) : null}
      </form>
    </Dialog>
  )
}

/** The access a teammate has, in the words the invite form offers: Admin, Write or Read. */
function accessLabel(access: TeamMember["access"]) {
  if (access === "admin") {
    return "Admin"
  }
  return access === "write" ? "Write" : "Read"
}

/** A teammate's status as its badge reads: Active once the account exists, else Pending. */
function statusLabel(status: TeamMember["status"]) {
  return status === "created" ? "Active" : "Pending"
}

/** Search matches what each row shows: the email, the status's badge and the access's label. */
function teamMemberMatches(member: TeamMember, search: string) {
  const query = search.trim().toLowerCase()
  return (
    query.length === 0 ||
    [member.email, statusLabel(member.status), accessLabel(member.access)].some((value) =>
      value.toLowerCase().includes(query),
    )
  )
}

/**
 * Why an admin's checkbox is disabled, as its tooltip and its description: the API removes no
 * admin. The owner is the signed-in account when its role is owner; the team list marks no owner.
 */
function removalNote(member: TeamMember, session: AdminSessionResponse | undefined) {
  if (member.access !== "admin") return undefined
  const owner =
    session?.authenticated === true &&
    session.session.account.role === "owner" &&
    session.session.account.id === member.id
  return owner ? "The owner can't be removed" : "Admins can't be removed here"
}

export function AdminTeamPage() {
  const queryClient = useQueryClient()
  const query = useQuery({ queryFn: loadAdminTeam, queryKey: ["admin-team"] })
  // The session the app shell loaded, read from the cache: no request of its own.
  const session = queryClient.getQueryData<AdminSessionResponse>(["admin-session"])
  const [search, setSearch] = useState("")
  const [selected, setSelected] = useState<string[]>([])
  const [showInvite, setShowInvite] = useState(false)
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")
  const catalog = query.data
  const members = (catalog?.members ?? []).filter((member) => teamMemberMatches(member, search))
  const selectedMembers = (catalog?.members ?? []).filter((member) => selected.includes(member.id))
  const selectedPending =
    selectedMembers.length > 0 &&
    selectedMembers.every((member) => member.status === "pending" && member.access !== "admin")

  function receiveCatalog(data: AdminTeamCatalog, message: string) {
    queryClient.setQueryData(["admin-team"], data)
    setSelected([])
    setActionError("")
    setNotice(message)
  }

  const create = useMutation({
    mutationFn: createAdminTeamMember,
    onSuccess(data) {
      setShowInvite(false)
      receiveCatalog(data, "Invitation sent.")
    },
  })
  const invite = useMutation({
    mutationFn: inviteAdminTeamMembers,
    onSuccess(data) {
      receiveCatalog(data, "Invitation sent.")
    },
    onError(error) {
      setNotice("")
      setActionError(error instanceof Error ? error.message : "Invitation could not be sent.")
    },
  })
  const remove = useMutation({
    mutationFn: deleteAdminTeamMembers,
    onSuccess(data) {
      receiveCatalog(data, "Teammate removed.")
    },
    onError(error) {
      setNotice("")
      setActionError(error instanceof Error ? error.message : "Teammate could not be removed.")
    },
  })

  if (query.isError) {
    return (
      <section className="admin-stack admin-page">
        <SettingsHeader active="team" />
        <LoadError
          detail={errorDetail(query.error)}
          message="Team settings could not load"
          onRetry={() => void query.refetch()}
          retrying={query.isFetching}
        />
      </section>
    )
  }

  const writable = Boolean(catalog?.writable)
  const canInvite = writable && Boolean(catalog?.inviteDeliveryAvailable)
  const selectableMembers = members.filter((member) => member.access !== "admin")
  const allVisibleSelected =
    selectableMembers.length > 0 &&
    selectableMembers.every((member) => selected.includes(member.id))

  function toggle(id: string) {
    setSelected((current) =>
      current.includes(id) ? current.filter((each) => each !== id) : [...current, id],
    )
  }

  const columns: Column<TeamMember>[] = [
    {
      // The checkbox sits in the row header, beside the email it selects. The owner's is disabled,
      // and says why.
      cell: (member) => {
        const note = removalNote(member, session)
        const noteId = `admin-team-note-${member.id}`
        return (
          // Inline, not the flex .admin-select-cell: the email wraps at its breaks beside the box,
          // and the row reads as one line of text.
          <span className="admin-team-email">
            <input
              aria-describedby={note ? noteId : undefined}
              aria-label={`Select ${member.email}`}
              checked={selected.includes(member.id)}
              className="tny-choice-input"
              disabled={member.access === "admin"}
              onChange={() => toggle(member.id)}
              title={note}
              type="checkbox"
            />
            {breakable(member.email)}
            {note ? (
              <span className="tny-visually-hidden" id={noteId}>
                {note}
              </span>
            ) : null}
          </span>
        )
      },
      header: "Email",
      // Every teammate but the owner, as the rows' own checkboxes allow.
      headerControl: (
        <input
          aria-label="Select all teammates"
          checked={allVisibleSelected}
          className="tny-choice-input"
          disabled={selectableMembers.length === 0}
          onChange={() =>
            setSelected(allVisibleSelected ? [] : selectableMembers.map((member) => member.id))
          }
          type="checkbox"
        />
      ),
      key: "email",
    },
    {
      cell: (member) =>
        member.status === "created" ? (
          <Badge tone="success">{statusLabel(member.status)}</Badge>
        ) : (
          <Badge>{statusLabel(member.status)}</Badge>
        ),
      header: "Status",
      key: "status",
      priority: 2,
    },
    { cell: (member) => accessLabel(member.access), header: "Access", key: "access" },
  ]

  return (
    <section className="admin-stack admin-page">
      <SettingsHeader active="team" />
      {catalog && !catalog.inviteDeliveryAvailable ? (
        <p className="admin-note">
          Team email invitations are unavailable until a delivery provider is connected. Existing
          non-admin teammates can still be removed.
        </p>
      ) : null}
      <div className="admin-toolbar">
        <SearchInput label="Search teammates" onSearch={setSearch} value={search} />
        <div className="admin-actions">
          <Button
            disabled={!canInvite}
            onClick={() => {
              setActionError("")
              create.reset()
              setShowInvite(true)
            }}
          >
            <NavIcon name="plus" />
            Invite teammate
          </Button>
        </div>
      </div>
      {/* Mounted empty, so the count is announced as a selection starts; the actions show with it. */}
      <div className="admin-actions admin-selection">
        <p className="admin-note" role="status">
          {selected.length > 0 ? `${selected.length} selected` : ""}
        </p>
        {selected.length > 0 ? (
          <>
            <Button
              disabled={!canInvite || !selectedPending}
              loading={invite.isPending}
              onClick={() => invite.mutate(selected)}
              size="sm"
              variant="secondary"
            >
              Resend invite
            </Button>
            <Button
              disabled={!writable}
              loading={remove.isPending}
              onClick={() => remove.mutate(selected)}
              size="sm"
              variant="danger"
            >
              Remove
            </Button>
          </>
        ) : null}
      </div>
      {actionError ? (
        <p className="tny-error" role="alert">
          {actionError}
        </p>
      ) : null}
      <p className="admin-note" role="status">
        {notice}
      </p>
      <Table
        caption="Team"
        captionHidden
        columns={columns}
        empty={
          <p className="admin-note">
            {search.trim() ? `No teammates match “${search.trim()}”.` : "No teammates yet."}
          </p>
        }
        loading={query.isPending}
        loadingLabel="Loading team…"
        rowKey={(member) => member.id}
        rows={members}
      />
      {showInvite ? (
        <TeamInviteForm
          error={create.error instanceof Error ? create.error.message : ""}
          onClose={() => setShowInvite(false)}
          onSave={(member) => create.mutate(member)}
          saving={create.isPending}
        />
      ) : null}
    </section>
  )
}
